import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { fetchGlobalSearchResults, fetchRecentItemsService } from './globalSearch.service'

const initialState = {
    searchFetchStatus: 'idle',
    searchFetchError: null,
    latestQuery: '',
    sections: [],
    recentItems: [],
    recentFetchStatus: 'idle',
}

const SECTION_KEY_BY_TITLE = {
    'support task': 'supportTask',
    'support tasks': 'supportTask',
    task: 'task',
    tasks: 'task',
    'development task': 'task',
    'development tasks': 'task',
    project: 'project',
    projects: 'project',
    // assignee: 'assignee',
}

const resolveSectionKey = (title) =>
    SECTION_KEY_BY_TITLE[String(title || '').toLowerCase().trim()] || null

const mapApiSections = (sections = []) =>
    sections.map((section, index) => ({
        key: `${section?.title || 'section'}-${index}`,
        title: section?.title || 'Results',
        total: section?.total || 0,
        count: section?.count || 0,
        isNext: Boolean(section?.isNext),
        limit: section?.limit || 0,
        skip: section?.skip || 0,
        items: Array.isArray(section?.data) ? section.data : [],
    }))

const mergeSectionItems = (existingItems = [], incomingItems = []) => {
    const seen = new Set(
        existingItems.map((item) => String(item?._id || item?.id || ''))
    )
    const merged = [...existingItems]
    for (const item of incomingItems) {
        const id = String(item?._id || item?.id || '')
        if (id && seen.has(id)) continue
        if (id) seen.add(id)
        merged.push(item)
    }
    return merged
}

export const fetchGlobalSearch = createAsyncThunk(
    'globalSearch/fetch',
    async (payload = {}, { rejectWithValue }) => {
        try {
            const response = await fetchGlobalSearchResults(payload)
            return response
        } catch (error) {
            return rejectWithValue(error?.response?.data || error?.message)
        }
    }
)

export const fetchRecentItems = createAsyncThunk(
    'globalSearch/fetchRecents',
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetchRecentItemsService()
            return response
        } catch (error) {
            return rejectWithValue(error?.response?.data || error?.message)
        }
    }
)

const globalSearchSlice = createSlice({
    name: 'globalSearchSlice',
    initialState,
    reducers: {
        clearGlobalSearch: (state) => {
            state.searchFetchStatus = 'idle'
            state.searchFetchError = null
            state.sections = []
            state.latestQuery = ''
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGlobalSearch.pending, (state, action) => {
                state.searchFetchStatus = 'loading'
                state.searchFetchError = null
                state.latestQuery = action.meta.arg?.search || ''
            })
            .addCase(fetchGlobalSearch.fulfilled, (state, action) => {
                const queryFromAction = action.meta.arg?.search || ''
                if (queryFromAction !== state.latestQuery) {
                    return
                }

                const payloadData = action.payload?.data || {}
                const sections = Array.isArray(payloadData?.data)
                    ? payloadData.data
                    : []
                const mapped = mapApiSections(sections)
                const append = Boolean(action.meta.arg?.append)
                const appendSectionKey = action.meta.arg?.appendSectionKey

                state.searchFetchStatus = 'loaded'

                if (!append || !appendSectionKey) {
                    state.sections = mapped
                    return
                }

                const incoming = mapped.find(
                    (section) =>
                        resolveSectionKey(section.title) === appendSectionKey
                )
                if (!incoming) return

                const existingIdx = state.sections.findIndex(
                    (section) =>
                        resolveSectionKey(section.title) === appendSectionKey
                )

                if (existingIdx < 0) {
                    state.sections = [...state.sections, incoming]
                    return
                }

                const existing = state.sections[existingIdx]
                const mergedItems = mergeSectionItems(
                    existing.items,
                    incoming.items
                )
                state.sections[existingIdx] = {
                    ...existing,
                    total: incoming.total || existing.total,
                    count: mergedItems.length,
                    isNext: incoming.isNext,
                    limit: incoming.limit,
                    skip: incoming.skip,
                    items: mergedItems,
                }
            })
            .addCase(fetchGlobalSearch.rejected, (state, action) => {
                state.searchFetchStatus = 'error'
                state.searchFetchError =
                    action.payload || 'Failed to fetch global search results'
                if (!action.meta.arg?.append) {
                    state.sections = []
                }
            })
            .addCase(fetchRecentItems.pending, (state) => {
                state.recentFetchStatus = 'loading'
            })
            .addCase(fetchRecentItems.fulfilled, (state, action) => {
                const payloadData = action.payload?.data || {}
                const items = Array.isArray(payloadData?.data)
                    ? payloadData.data
                    : []
                state.recentFetchStatus = 'loaded'
                state.recentItems = items
            })
            .addCase(fetchRecentItems.rejected, (state) => {
                state.recentFetchStatus = 'error'
                state.recentItems = []
            })
    },
})

export const globalSearchState = (s) => s.globalSearchSlice || initialState
export const { clearGlobalSearch } = globalSearchSlice.actions
export default globalSearchSlice.reducer

